import { useParams, useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { useCartStore } from '@/store/cartStore'
import Button from '@/components/common/Button'
import { formatCurrency } from '@/utils/helpers'
import toast from 'react-hot-toast'
import { FaArrowLeft, FaShoppingCart, FaCheck } from 'react-icons/fa'

// Mock product data - في المستقبل سيتم جلبها من API
const PRODUCTS: Record<string, any> = {
  'acc-halter': {
    id: 'acc-halter',
    nameKey: 'holder',
    price: 9.99,
    image: '🖼️',
    descriptionKey: 'holderDesc',
    category: 'ACCESSORIES',
    inStock: true,
  },
  'acc-warnweste': {
    id: 'acc-warnweste',
    nameKey: 'vest',
    price: 4.99,
    image: '🦺',
    descriptionKey: 'vestDesc',
    category: 'ACCESSORIES',
    inStock: true,
  },
  'acc-warndreieck': {
    id: 'acc-warndreieck',
    nameKey: 'triangle',
    price: 8.99,
    image: '⚠️',
    descriptionKey: 'triangleDesc',
    category: 'ACCESSORIES',
    inStock: true,
  },
  'acc-parkscheibe': {
    id: 'acc-parkscheibe',
    nameKey: 'parking',
    price: 2.99,
    image: '🅿️',
    descriptionKey: 'parkingDesc',
    category: 'ACCESSORIES',
    inStock: true,
  },
  'acc-verbandskasten': {
    id: 'acc-verbandskasten',
    nameKey: 'firstAid',
    price: 19.99,
    image: '🏥',
    descriptionKey: 'firstAidDesc',
    category: 'ACCESSORIES',
    inStock: true,
  },
  'acc-feuerloscher': {
    id: 'acc-feuerloscher',
    nameKey: 'extinguisher',
    price: 29.99,
    image: '🧯',
    descriptionKey: 'extinguisherDesc',
    category: 'ACCESSORIES',
    inStock: true,
  },
  'acc-warnkegel': {
    id: 'acc-warnkegel',
    nameKey: 'cones',
    price: 12.99,
    image: '🔺',
    descriptionKey: 'conesDesc',
    category: 'ACCESSORIES',
    inStock: true,
  },
}

const ProductDetail = () => {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { addItem, items } = useCartStore()

  const product = id ? PRODUCTS[id] : null
  const isInCart = product && items.some((item) => item.productId === product.id)

  if (!product) {
    return (
      <div className="bg-dark-950 min-h-screen py-12 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <h2 className="text-2xl font-bold text-gray-300 mb-4">{t('productDetail.notFound')}</h2>
          <Link
            to="/shop"
            className="inline-block px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-semibold transition-colors"
          >
            {t('productDetail.backToShop')}
          </Link>
        </motion.div>
      </div>
    )
  }

  const handleAddToCart = () => {
    addItem({
      productId: product.id,
      productName: t(`accessories.${product.nameKey}`),
      price: product.price,
      quantity: 1,
    })

    toast.success(t('toast.productAdded'))
  }

  return (
    <div className="bg-dark-950 min-h-screen py-6 sm:py-8 relative overflow-hidden">
      <div className="fixed inset-0 bg-grid-pattern opacity-20"></div>
      <div className="absolute inset-0 bg-gradient-mesh opacity-30"></div>

      <div className="container-custom px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-primary-400 transition-colors mb-6"
        >
          <FaArrowLeft className="w-4 h-4" />
          <span>{t('common.back')}</span>
        </motion.button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Product Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="card-hover p-8 lg:p-12 flex items-center justify-center"
          >
            <div className="text-9xl lg:text-[12rem] hover:scale-110 transition-transform duration-300">
              {product.image}
            </div>
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            {/* Title */}
            <div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-black mb-4">
                <span className="text-gradient bg-clip-text text-transparent bg-gradient-to-r from-primary-400 via-purple-400 to-primary-400">
                  {t(`accessories.${product.nameKey}`)}
                </span>
              </h1>
              <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-primary-500 to-transparent"></div>
            </div>

            {/* Price */}
            <div className="flex items-center gap-4">
              <span className="text-4xl sm:text-5xl font-bold text-primary-400">
                {formatCurrency(product.price)}
              </span>
              {product.inStock && (
                <span className="px-4 py-2 bg-green-500/10 border border-green-500/20 text-green-400 rounded-lg text-sm font-semibold flex items-center gap-2">
                  <FaCheck className="w-4 h-4" />
                  {t('productDetail.inStock')}
                </span>
              )}
            </div>

            {/* Description */}
            <div className="card-hover p-6">
              <h3 className="text-lg font-bold text-gray-100 mb-3">{t('productDetail.description')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t(`accessories.${product.descriptionKey}`)}
              </p>
            </div>

            {/* Features */}
            <div className="card-hover p-6">
              <h3 className="text-lg font-bold text-gray-100 mb-4">{t('productDetail.features')}</h3>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-gray-400">
                  <FaCheck className="w-4 h-4 text-green-400" />
                  <span>{t('productDetail.feature1')}</span>
                </li>
                <li className="flex items-center gap-2 text-gray-400">
                  <FaCheck className="w-4 h-4 text-green-400" />
                  <span>{t('productDetail.feature2')}</span>
                </li>
                <li className="flex items-center gap-2 text-gray-400">
                  <FaCheck className="w-4 h-4 text-green-400" />
                  <span>{t('productDetail.feature3')}</span>
                </li>
              </ul>
            </div>

            {/* Add to Cart */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={handleAddToCart}
                disabled={isInCart}
                className="flex-1 flex items-center justify-center gap-2"
                size="lg"
              >
                {isInCart ? (
                  <>
                    <FaCheck className="w-5 h-5" />
                    {t('productDetail.inCart')}
                  </>
                ) : (
                  <>
                    <FaShoppingCart className="w-5 h-5" />
                    {t('productDetail.addToCart')}
                  </>
                )}
              </Button>
              <Link
                to="/checkout"
                className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-500 via-purple-600 to-purple-700 hover:from-purple-600 hover:via-purple-700 hover:to-purple-800 text-white rounded-lg font-semibold transition-all duration-300 shadow-lg shadow-purple-500/50 text-center flex items-center justify-center"
              >
                {t('productDetail.buyNow')}
              </Link>
            </div>

            {/* Related Products */}
            <div className="card-hover p-6">
              <h3 className="text-lg font-bold text-gray-100 mb-4">{t('productDetail.relatedProducts')}</h3>
              <div className="grid grid-cols-4 gap-4">
                {Object.values(PRODUCTS)
                  .filter((p) => p.id !== product.id)
                  .slice(0, 4)
                  .map((relatedProduct) => (
                    <Link
                      key={relatedProduct.id}
                      to={`/product/${relatedProduct.id}`}
                      className="text-center group"
                    >
                      <div className="text-4xl mb-2 group-hover:scale-110 transition-transform">
                        {relatedProduct.image}
                      </div>
                      <p className="text-xs text-gray-400 group-hover:text-primary-400 transition-colors">
                        {t(`accessories.${relatedProduct.nameKey}`)}
                      </p>
                    </Link>
                  ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default ProductDetail
